import { addMeetingLog, MeetingLogLevel } from 'flow/utils/meeting-utils';
import {
    askAndGetConnectedMediaDevices,
    CAMERA,
    MICROPHONE,
    permissionTypes,
    getMediaDeviceTypeSystemPermissionStatusOnMacOS,
    probeMediaPermission,
} from 'flow/utils/media-utils';
import {
    getPreferredAudioOutputId,
    getPreferredMicrophoneId,
} from 'flow/utils/browser-utils';
import {
    selectMeetingIsActive,
    selectAudioOutputDeviceList,
    selectMicDeviceList,
    selectVideoDeviceList,
    selectMicInUse,
    selectAudioOutputInUse,
    selectVideoInUse,
    selectLobbyMicDeviceInUse,
    selectLobbyAudioDeviceInUse,
    selectLobbyVideoDeviceInUse,
    selectSenderAudioActive,
    selectActiveMeetingCallSession,
} from 'flow/selectors/meeting-selectors';
import { stopStream } from 'flow/utils/video-utils';

export const checkHasDevicePermission = async (deviceKind) => {
    if (deviceKind !== CAMERA && deviceKind !== MICROPHONE) {
        return false;
    }

    //If Electron, check systemRights
    const hasSystemRights =
        await getMediaDeviceTypeSystemPermissionStatusOnMacOS(deviceKind);
    if (hasSystemRights !== permissionTypes.GRANTED) {
        return false;
    }

    try {
        return await probeMediaPermission(
            deviceKind === CAMERA ? { video: true } : { audio: true },
        );
    } catch (_error) {
        return false;
    }
};

export const checkAudioAndVideoPermissions = async () => {
    try {
        return await probeMediaPermission({ audio: true, video: true });
    } catch (_error) {
        return false;
    }
};

export const findDeviceFromId = (deviceList, deviceId) => {
    return deviceList.find((o) => o.deviceId === deviceId);
};

export const getDefaultDevices = async () => {
    const devicesLists = await getAvailableDevicesLists();
    const { micList, audioList, videoList } = devicesLists;

    const defaultMic = getDeviceWithDefaultId(micList);
    const defaultAudio = getDeviceWithDefaultId(audioList);
    const defaultVideo = getDeviceWithDefaultId(videoList);

    return {
        defaultMic,
        defaultAudio,
        defaultVideo,
    };
};

export const getAvailableDevicesLists = async () => {
    // Prompt video permission toggle
    await askAndGetConnectedMediaDevices(true, false);
    // Prompt audio permission toggle AND get the DeviceInfoArray
    const allDevicesList = await askAndGetConnectedMediaDevices(false, true);
    const nonEmptyDevicesList = allDevicesList.filter((o) => o.deviceId !== '');

    const MIC_KIND = 'audioinput';
    const AUDIO_KIND = 'audiooutput';
    const VIDEO_KIND = 'videoinput';

    const micList = nonEmptyDevicesList.filter((o) => o.kind === MIC_KIND);
    const audioList = nonEmptyDevicesList.filter((o) => o.kind === AUDIO_KIND);
    const videoList = nonEmptyDevicesList.filter((o) => o.kind === VIDEO_KIND);

    return {
        micList,
        audioList,
        videoList,
    };
};

export const getMicDeviceFromLocalStorage = (state) => {
    const micIDLocalStorage = getPreferredMicrophoneId();
    const micList = selectMicDeviceList(state);
    const micDevice = findDeviceFromId(micList, micIDLocalStorage);
    return micDevice;
};

export const getAudioDeviceFromLocalStorage = (state) => {
    const audioOutputLocalStorage = getPreferredAudioOutputId();
    const deviceList = selectAudioOutputDeviceList(state);
    const audioDevice = findDeviceFromId(deviceList, audioOutputLocalStorage);
    return audioDevice;
};

export const findDeviceRemoved = async (state) => {
    const devicesLists = await getAvailableDevicesLists();
    const { micList, audioList, videoList } = devicesLists;

    const oldMicList = selectMicDeviceList(state);
    const oldAudioOutputList = selectAudioOutputDeviceList(state);
    const oldVideoList = selectVideoDeviceList(state);

    const micDiff = findRemovedDevice(micList, oldMicList);
    const audioDiff = findRemovedDevice(audioList, oldAudioOutputList);
    const videoDiff = findRemovedDevice(videoList, oldVideoList);

    return { micDiff, audioDiff, videoDiff };
};

export const findRemovedDevice = (newDeviceList, oldDeviceList) => {
    return oldDeviceList.filter(
        ({ groupId: id1 }) =>
            !newDeviceList.some(({ groupId: id2 }) => id2 === id1),
    );
};

const getDeviceWithDefaultId = (list) => {
    return list.find((o) => o.deviceId === 'default') || list[0];
};

export const hasRemovedCurrentMic = (micDiffList, state) => {
    const meetingIsActive = selectMeetingIsActive(state);
    if (micDiffList && micDiffList.length > 0) {
        const currentMicInUse = meetingIsActive
            ? selectMicInUse(state)
            : selectLobbyMicDeviceInUse(state);
        return findDeviceFromId(micDiffList, currentMicInUse.deviceId);
    }
    return false;
};

export const hasRemovedCurrentAudio = (audioDiffList, state) => {
    const meetingIsActive = selectMeetingIsActive(state);
    if (audioDiffList && audioDiffList.length > 0) {
        const currentAudioInUse = meetingIsActive
            ? selectAudioOutputInUse(state)
            : selectLobbyAudioDeviceInUse(state);
        return audioDiffList.find(
            (device) => device.deviceId === currentAudioInUse.deviceId,
        );
    }
    return false;
};

export const hasRemovedCurrentVideo = (videoDiffList, state) => {
    const meetingIsActive = selectMeetingIsActive(state);
    if (videoDiffList && videoDiffList.length > 0) {
        const currentVideoInUse = meetingIsActive
            ? selectVideoInUse(state)
            : selectLobbyVideoDeviceInUse(state);
        return videoDiffList.find(
            (device) => device.deviceId === currentVideoInUse.deviceId,
        );
    }
    return false;
};

export const renegotiateAudio =
    (micDevice, micList) => async (dispatch, getState) => {
        let deviceId;
        if (
            micDevice &&
            micDevice.deviceId &&
            micDevice.deviceId === 'default'
        ) {
            const sameDevice = micList.find(
                (mic) =>
                    mic.groupId &&
                    mic.groupId === micDevice.groupId &&
                    mic.deviceId !== 'default',
            );
            deviceId = sameDevice && sameDevice.deviceId;
        } else {
            deviceId = micDevice && micDevice.deviceId;
        }
        dispatch(
            addMeetingLog(
                `MeetingActions#renegotiateAudio: deviceId ${deviceId}`,
            ),
        );
        const stream = await navigator.mediaDevices.getUserMedia({
            video: false,
            audio: { deviceId: deviceId ? { exact: deviceId } : undefined },
        });
        const audioTracks = stream.getAudioTracks();
        const audioActive = selectSenderAudioActive(getState());
        if (!audioActive) {
            // Makes sure that the reconnected stream, if muted, does not send media post renegotiate
            dispatch(
                addMeetingLog(
                    'MeetingActions#renegotiateAudio: not doing anything, audio muted',
                    MeetingLogLevel.WARNING,
                ),
            );
            audioTracks[0].enabled = false;
        }
        const callSession = selectActiveMeetingCallSession(getState());
        if (!callSession) {
            dispatch(
                addMeetingLog(
                    'MeetingActions#renegotiateAudio: no callSession found',
                    MeetingLogLevel.ERROR,
                ),
            );
            // Nothing will send or stop this microphone, so release it now
            await stopStream(stream);
            return false;
        }
        try {
            // sender.track only points at the new track once replaceTrack
            // resolves, and setSenderAudioStream reads it from there
            await Promise.all(
                callSession.connection
                    .getSenders()
                    .filter((r) => r.track?.kind !== 'video')
                    .map((r) => r.replaceTrack(audioTracks[0])),
            );
        } catch (e) {
            dispatch(
                addMeetingLog(
                    `MeetingActions#renegotiateAudio: swapping the microphone failed: ${e}`,
                    MeetingLogLevel.ERROR,
                ),
            );
            // The call keeps sending the old microphone, so release the new one
            await stopStream(stream);
            return false;
        }
        callSession.renegotiate(undefined, () => {
            dispatch(
                addMeetingLog(
                    'MeetingActions#renegotiateAudio: renegotiate done',
                ),
            );
        });
        return true;
    };
